import { ReadonlyVec3 } from 'gl-matrix';

/**
 * Keys a margin preview is published and consumed under. Two requests share a
 * key exactly when they produce the same surface: the same margins around the
 * same structure at the same content version, so a preview of another
 * structure, or of this one before an edit, never passes for the current
 * one. Margins are signed mm, negative contracting.
 */

/** The structure a margin is taken around, at a content version. */
export interface MarginSource {
    roiNumber: number;
    sdfVersion: number;
}

const sourceToken = ({ roiNumber, sdfVersion }: MarginSource): string =>
    `${roiNumber}@${sdfVersion}`;

export const symmetricPreviewKey = (
    source: MarginSource,
    marginMm: number
): string => `sym:${sourceToken(source)}|${marginMm}`;

export const asymmetricPreviewKey = (
    source: MarginSource,
    negLpsMm: ReadonlyVec3,
    posLpsMm: ReadonlyVec3
): string =>
    `asym:${sourceToken(source)}` +
    `|${negLpsMm[0]},${negLpsMm[1]},${negLpsMm[2]}` +
    `|${posLpsMm[0]},${posLpsMm[1]},${posLpsMm[2]}`;

/**
 * All margins zero: dims the others and highlights the source. Never handed
 * off, so it names no source.
 */
export const IDENTITY_PREVIEW_KEY = 'identity';
